import { useState, useEffect, useRef } from 'react';
import { useTranslate } from '@awardit/react-use-translate';
import { createForOfIteratorHelperLoose as _createForOfIteratorHelperLoose } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { debounce, deepEquals } from 'diskho';
import { useHistory } from 'react-router-dom';
import { usePrevious } from '../../helpers/use-previous.js';
import { getInputFilters, getInputSort, getPage, getSearchFromFilter } from '@crossroads/shop-state/filter';
import { getAvailableFilters, useUpdateProductList, sortValueToInput } from './helpers.js';

var useFilter = function useFilter(_ref) {
  var _ref2;
  var productList = _ref.productList,
    _ref$usePoints = _ref.usePoints,
    usePoints = _ref$usePoints === void 0 ? false : _ref$usePoints,
    loading = _ref.loading,
    load = _ref.load,
    sortValues = _ref.sortValues,
    _ref$priceToPoints = _ref.priceToPoints,
    priceToPoints = _ref$priceToPoints === void 0 ? function (price) {
      return price;
    } : _ref$priceToPoints,
    _ref$incVat = _ref.incVat,
    incVat = _ref$incVat === void 0 ? true : _ref$incVat,
    _ref$autoFilterMinPoi = _ref.autoFilterMinPoints,
    autoFilterMinPoints = _ref$autoFilterMinPoi === void 0 ? 0 : _ref$autoFilterMinPoi;
  var _useHistory = useHistory(),
    push = _useHistory.push,
    location = _useHistory.location,
    action = _useHistory.action,
    replace = _useHistory.replace;
  var t = useTranslate();
  var floorPrice = function floorPrice(price) {
    return Math.floor(priceToPoints(price));
  };
  var ceilPrice = function ceilPrice(price) {
    return Math.ceil(priceToPoints(price));
  };
  var filters = getInputFilters(location).filter(function (f) {
    return !f.code.startsWith(usePoints ? "price" : "points:");
  });
  var sort = getInputSort(location);
  var page = getPage(location);
  var _useState = useState(false),
    updating = _useState[0],
    setUpdating = _useState[1];
  var _useState2 = useState((_ref2 = autoFilterMinPoints > 0) != null ? _ref2 : false),
    autoFilterMinPointsActive = _useState2[0],
    setAutoFilterMinPoints = _useState2[1];
  var filterMinPointsObject = {
    __typename: "ProductFilterBoolean",
    code: "within_balance",
    label: t("FILTER.FILTERMENU.WITHIN_BALANCE")
  };
  useEffect(function () {
    if (updating && loading) {
      setUpdating(false);
    }
  }, [loading]);
  var _sortValues = sortValues || (Array.isArray(productList.sortableBy) ? productList.sortableBy : null);
  var activePriceFilter = filters.find(function (f) {
    return f.code.startsWith(usePoints ? "points:" : "price");
  });
  var priceFilter;
  for (var _iterator = _createForOfIteratorHelperLoose(productList.filterableBy), _step; !(_step = _iterator()).done;) {
    var fb = _step.value;
    if (fb.__typename === "ProductFilterPrice" && fb.code.startsWith(usePoints ? "points:" : "price")) {
      priceFilter = fb;
      break;
    }
  }
  var previousPriceFilter = usePrevious(priceFilter);
  var _useState3 = useState(priceFilter ? floorPrice(incVat ? priceFilter.incVat.min : priceFilter.exVat.min) : 0),
    minPrice = _useState3[0],
    setMinPrice = _useState3[1];
  var _useState4 = useState(priceFilter ? ceilPrice(incVat ? priceFilter.incVat.max : priceFilter.exVat.max) : 10000),
    maxPrice = _useState4[0],
    setMaxPrice = _useState4[1];
  var _useState5 = useState(sort ? sort.code + "_" + sort.order.toLowerCase() : null),
    sortValue = _useState5[0],
    setSortValue = _useState5[1];
  var availableFilters = getAvailableFilters(autoFilterMinPoints > 0 && maxPrice > autoFilterMinPoints ? [].concat(productList.filterableBy, [filterMinPointsObject]) : productList.filterableBy);
  var setRanges = function setRanges() {
    if (!activePriceFilter || typeof activePriceFilter.maxValue !== "number" || typeof activePriceFilter.minValue !== "number") {
      return {
        min: minPrice,
        max: maxPrice
      };
    }
    if (activePriceFilter.maxValue < activePriceFilter.minValue) {
      return {
        min: 0,
        max: activePriceFilter.maxValue
      };
    }
    return {
      min: activePriceFilter.minValue,
      max: activePriceFilter.maxValue
    };
  };
  var _useState6 = useState(setRanges()),
    localPriceRange = _useState6[0],
    setLocalPriceRange = _useState6[1];
  var _useState7 = useState(localPriceRange),
    priceRange = _useState7[0],
    setPriceRange = _useState7[1];
  var previousPriceRange = usePrevious(priceRange) || priceRange;
  var previousSortValueTemp = usePrevious(sortValue);
  var previousSortValue = previousSortValueTemp === undefined ? sortValue : previousSortValueTemp;
  var applyPriceRange = useRef(debounce(setPriceRange, 200)).current;
  var toggleFilter = function toggleFilter(filterInput) {
    setUpdating(true);
    var activeFilters = filters.map(function (x) {
      return x;
    });
    var code = filterInput.code;
    if (activeFilters.find(function (x) {
      return x.code === code;
    })) {
      activeFilters = activeFilters.filter(function (x) {
        return x.code !== code;
      });
      if (code.startsWith(usePoints ? "points:" : "price")) {
        setLocalPriceRange({
          min: minPrice,
          max: maxPrice
        });
        if (autoFilterMinPointsActive) {
          setAutoFilterMinPoints(false);
        }
      }
    } else if (code === filterMinPointsObject.code) {
      var _autoFilterMinPointsA;
      setAutoFilterMinPoints((_autoFilterMinPointsA = !autoFilterMinPointsActive) != null ? _autoFilterMinPointsA : true);
      var _activePriceFilter = activeFilters.find(function (x) {
        return x.code.startsWith(usePoints ? "points:" : "price");
      });
      if (_activePriceFilter !== undefined) {
        activeFilters = activeFilters.filter(function (x) {
          return x !== _activePriceFilter;
        });
        setLocalPriceRange({
          min: minPrice,
          max: maxPrice
        });
      }
    } else {
      activeFilters.push(filterInput);
    }
    push(location.pathname + getSearchFromFilter(activeFilters, sort, 0));
  };
  useEffect(function () {
    if (autoFilterMinPointsActive) {
      if (action === "POP" && localPriceRange.max !== autoFilterMinPoints) {
        setAutoFilterMinPoints(false);
      } else {
        setLocalPriceRange({
          min: minPrice,
          max: autoFilterMinPoints
        });
      }
    }
  }, [autoFilterMinPointsActive]);
  var clearAllFilters = function clearAllFilters() {
    setUpdating(true);
    push(location.pathname + getSearchFromFilter([], sort, 0));
    setLocalPriceRange({
      min: minPrice,
      max: maxPrice
    });
    setAutoFilterMinPoints(false);
  };
  useUpdateProductList(load);

  // Reset min/max-price for slider if range on pricefilter changed
  useEffect(function () {
    if (priceFilter && !deepEquals(priceFilter, previousPriceFilter)) {
      var pf = priceFilter;
      var newMaxPrice = ceilPrice(incVat ? pf.incVat.max : pf.exVat.max);
      var newMinPrice = floorPrice(incVat ? pf.incVat.min : pf.exVat.min);
      if (newMaxPrice !== maxPrice || newMinPrice !== minPrice) {
        setLocalPriceRange({
          min: localPriceRange.min <= newMinPrice || localPriceRange.min === minPrice ? newMinPrice : localPriceRange.min,
          max: localPriceRange.max >= newMaxPrice || localPriceRange.max === maxPrice ? newMaxPrice : localPriceRange.max
        });
        setMinPrice(newMinPrice);
        setMaxPrice(newMaxPrice);
        if (autoFilterMinPointsActive && newMaxPrice < autoFilterMinPoints) {
          var activeFilters = filters.map(function (x) {
            return x;
          });
          var _activePriceFilter2 = activeFilters.find(function (x) {
            return x.code.startsWith(usePoints ? "points:" : "price");
          });
          setAutoFilterMinPoints(false);
          activeFilters = activeFilters.filter(function (x) {
            return x !== _activePriceFilter2;
          });
          replace(location.pathname + getSearchFromFilter(activeFilters, sort, 0));
        }
      }
    }
  }, [priceFilter]);
  useEffect(function () {
    applyPriceRange(localPriceRange);
  }, [applyPriceRange, localPriceRange]);

  // Update URL when applying sorts and price filter (not normal filters)
  useEffect(function () {
    if (sortValue !== previousSortValue || !deepEquals(priceRange, previousPriceRange)) {
      var _sort = sortValue ? sortValueToInput(sortValue) : null;
      var activeFilters = filters.map(function (x) {
        return x;
      }).filter(function (x) {
        return !x.code.startsWith(usePoints ? "points:" : "price");
      });
      if (priceRange.min !== minPrice || priceRange.max !== maxPrice) {
        var _priceFilter;
        activeFilters.push({
          code: ((_priceFilter = priceFilter) == null ? void 0 : _priceFilter.code) || "",
          minValue: priceRange.min !== minPrice ? priceRange.min : minPrice,
          maxValue: priceRange.max !== maxPrice ? priceRange.max : maxPrice
        });
        if (autoFilterMinPointsActive && priceRange.max !== autoFilterMinPoints) {
          setAutoFilterMinPoints(false);
        }
      }
      push(location.pathname + getSearchFromFilter(activeFilters, _sort, 0));
    }
  }, [sortValue, priceRange]);
  return {
    sort: {
      setValue: setSortValue,
      values: _sortValues,
      value: sortValue
    },
    price: {
      min: minPrice,
      max: maxPrice,
      range: localPriceRange,
      setRange: function setRange(r) {
        if (!loading) {
          setUpdating(true);
          setLocalPriceRange(r);
        }
      }
    },
    filters: availableFilters,
    active: {
      filters: filters,
      sort: sort,
      page: page
    },
    toggleFilter: toggleFilter,
    clearAllFilters: clearAllFilters,
    totalCount: productList.totalCount,
    usePoints: usePoints,
    loading: loading,
    visible: loading || filters.length > 0 || productList.totalCount > 1 || updating,
    autoFilterMinPointsActive: autoFilterMinPointsActive
  };
};

export { useFilter };
//# sourceMappingURL=index.js.map
